import React from 'react'

import { Link } from 'react-router-dom'
import { reverse } from 'named-urls'
import moment from 'moment'

import { Box, Button, Chip } from '@material-ui/core'

import {
  BookmarkBorderOutlined,
  FilterNoneOutlined,
  CalendarTodayOutlined,
  AccountBalanceWalletOutlined,
  ErrorOutlineOutlined,
  EventAvailableOutlined,
} from '@material-ui/icons'

import { routes } from 'routes'
import * as service from 'service'
import useSnackbar from 'hooks/useSnackbar'
import helpers from 'helpers'
import useFetch from 'hooks/useFetch'

import { Container, ContentHeader, ClientInfo } from 'components'
import { Permitted } from 'components/Permissions'
import { ContainerSummary, ItemSummary } from 'components/Summary'
import { LogButton } from 'components/Logs'
import EntryInfo from './components/EntryInfo'
import ContractDisable from './components/ContractDisable'
import ContractHistory from './components/ContractHistory'
import InvoiceInfo from './components/InvoiceInfo'
import PlansInfo from './components/PlansInfo'
import AutoPayment from './components/AutoPayment'
import ContractSuspended from './components/ContractSuspended'
import ReactivateContract from './components/ReactivateContract'

const ContractsShow = ({ match }) => {
  const contractId = match.params.contractId
  const snackbar = useSnackbar()

  const { response: responseContract, isLoading, refresh } = useFetch(
    service.recurrence.contracts.get,
    {
      contractId,
    },
  )

  const afterEvent = () => {
    refresh()
  }

  const showReactivateButton = status => {
    if (status === 'overdue' && window.location.host.split('.')[0] === 'traycorp') {
      return true
    }
    return false
  }

  const activate = async () => {
    try {
      await service.recurrence.contracts.forceActivate({
        contractId: contractId
      })
    } catch {
      snackbar.open({
        message: 'Falha ao reativar.',
        variant: 'error',
      })
    } finally {
      afterEvent()
    }
  }

  return (
    <Container>
      <ContentHeader title="Assinatura" subtitle="Detalhes">
        <Box display="flex">
          { showReactivateButton(responseContract && responseContract.data.contract.status) ? 
            <Box ml={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={activate}
              >
                Reativar
              </Button>
            </Box> : null }
          <LogButton to={reverse(routes.contracts.logs, { contractId })} />
          {showButtons(
            responseContract && responseContract.data.contract.status,
          ) ? (
            <Permitted tag="update_contract">
              <Box ml={2}>
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to={reverse(routes.contracts.edit, { contractId })}
                >
                  Editar
                </Button>
              </Box>
            </Permitted>
          ) : null}
        </Box>
      </ContentHeader>
      <Box mb={4}>
        <ContainerSummary isLoading={isLoading}>
          <ItemSummary
            title="Identificador"
            value={responseContract && responseContract.data.contract.id}
            icon={<BookmarkBorderOutlined color="disabled" />}
          />
          <ItemSummary
            title="Código"
            value={responseContract && responseContract.data.contract.code}
            icon={<FilterNoneOutlined color="disabled" />}
          />
          <ItemSummary
            title="Vencimento"
            value={
              responseContract && responseContract.data.contract.dayExpiration
            }
            icon={<CalendarTodayOutlined color="disabled" />}
          />
          <ItemSummary
            title="Tipo de cobrança"
            value={helpers.contracts.chargeType(
              responseContract && responseContract.data.contract,
            )}
            icon={<AccountBalanceWalletOutlined color="disabled" />}
          />
          <ItemSummary
            title="Data da contratação"
            value={
              responseContract &&
              moment(responseContract.data.contract.createdAt).format(
                'DD/MM/YYYY',
              )
            }
            icon={<EventAvailableOutlined color="disabled" />}
          />
          <ItemSummary
            title="Status"
            value={
              responseContract && (
                <Chip
                  label={helpers.contracts.status(
                    responseContract.data.contract.status,
                  )}
                  size="small"
                  style={{
                    backgroundColor: helpers.contracts.statusColor(
                      responseContract.data.contract.status,
                    ),
                    color: 'white',
                  }}
                />
              )
            }
            icon={<ErrorOutlineOutlined color="disabled" />}
          />
        </ContainerSummary>
      </Box>

      <Box mb={4}>
        {!isLoading && (
          <ClientInfo
            customer={
              responseContract && responseContract.data.contract.customer
            }
          />
        )}
      </Box>

      <Box mb={4}>
        {!isLoading && (
          <PlansInfo
            contract={responseContract && responseContract.data.contract}
          />
        )}
      </Box>

      <Box mb={4}>
        {!isLoading && (
          <InvoiceInfo
            contractId={responseContract && responseContract.data.contract.id}
            status={responseContract && responseContract.data.contract.status}
          />
        )}
      </Box>

      {!isLoading && (
        <AutoPayment
          contract={responseContract && responseContract.data.contract}
        />
      )}

      <Box mb={4}>
        {!isLoading && (
          <EntryInfo
            contractId={responseContract && responseContract.data.contract.id}
            status={responseContract && responseContract.data.contract.status}
          />
        )}
      </Box>

      {!isLoading && (
        <Box>
          <ContractHistory contract={responseContract?.data.contract} />
        </Box>
      )}

      <Permitted tag="destroy_contract">
        {responseContract?.data.contract.status === 'active' ? (
          <Box mt={4}>
            <ContractDisable
              contractId={responseContract && responseContract.data.contract.id}
              onEvent={afterEvent}
            />
          </Box>
        ) : null}
      </Permitted>
      {responseContract?.data.contract.status === 'active' && (
        <Box mt={4}>
          <ContractSuspended />
        </Box>
      )}
      {responseContract?.data.contract.status === 'paused' && (
        <Box mt={4}>
          <ReactivateContract
            contract={responseContract?.data.contract}
            onEvent={afterEvent}
          />
        </Box>
      )}
    </Container>
  )
}

const showButtons = status => {
  if (!status || status === undefined || status === null) return false

  if (status !== 'finished' && status !== 'canceled' && status !== 'inactive') {
    return true
  }

  return false
}

export default ContractsShow
